// Generated by OrchestKit Claude Plugin
// Created: 2026-09-12

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, DateHeure, Erreur, formaterDateHeure } from '../../composants/communs.tsx';
import {
  Badge,
  BoutonImprimer,
  DataTable,
  Nombre,
  Sheet,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { DemandeMotif } from '../../composants/motif.tsx';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireFournisseur } from './FormulaireFournisseur.tsx';
import type { FournisseurListe, ReponseFournisseurs } from './types.ts';

/**
 * Fournisseurs — amende Q-021.
 *
 * La fiche est GLOBALE : cet écran n'est pas cloisonné par boutique, et c'est
 * délibéré. Deux boutiques qui s'approvisionnent au même endroit doivent voir
 * la même fiche, sinon les volumes d'achat ne s'additionnent jamais.
 */
export function Fournisseurs(): ReactNode {
  const { peut } = useSession();
  const cache = useQueryClient();
  const [recherche, setRecherche] = useState('');
  const [saisie, setSaisie] = useState<FournisseurListe | 'nouveau' | null>(null);
  /** Fournisseur dont on attend le motif avant de basculer son activité. */
  const [aBasculer, setABasculer] = useState<FournisseurListe | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['fournisseurs', recherche],
    queryFn: () =>
      appeler<ReponseFournisseurs>(
        '/fournisseurs' +
          (recherche.trim() === '' ? '' : `?q=${encodeURIComponent(recherche.trim())}`),
      ),
  });

  /**
   * Bascule d'activité, avec le motif SAISI par celui qui agit.
   *
   * Le motif était auparavant une constante — « Desactivation depuis
   * l administration » — envoyée à chaque opération. Le serveur l'acceptait,
   * et le journal d'audit enregistrait une phrase qui n'apprend rien.
   * `RG-005` exige un motif pour qu'on retrouve le « pourquoi » des mois plus
   * tard, en cas de litige : une constante vide la règle de son sens.
   */
  const bascule = useMutation({
    mutationFn: (demande: { fournisseur: FournisseurListe; motif: string }) =>
      appeler(
        `/fournisseurs/${demande.fournisseur.id}/${demande.fournisseur.actif ? 'desactivation' : 'reactivation'}`,
        { methode: 'POST', corps: { motif: demande.motif } },
      ),
    onSuccess: () => {
      setABasculer(null);
      void cache.invalidateQueries({ queryKey: ['fournisseurs'] });
    },
  });

  return (
    <>
      <Entete
        titre="Fournisseurs"
        sous="Fiches communes à toutes les boutiques : un grossiste, une seule fiche."
      />

      {aBasculer !== null && (
        <div style={{ marginBottom: 16 }}>
          <DemandeMotif
            titre={
              aBasculer.actif
                ? `Désactiver ${aBasculer.nom}`
                : `Réactiver ${aBasculer.nom}`
            }
            description={
              aBasculer.actif
                ? 'La fiche disparaît des listes de saisie. Les réceptions déjà enregistrées à son nom restent intactes.'
                : 'La fiche redevient proposée au moment d’une réception.'
            }
            libelleAction={aBasculer.actif ? 'Désactiver' : 'Réactiver'}
            enCours={bascule.isPending}
            onValider={(motif) => {
              bascule.mutate({ fournisseur: aBasculer, motif });
            }}
            onAnnuler={() => {
              setABasculer(null);
            }}
          />
        </div>
      )}

      {saisie !== null && (
        <div style={{ marginBottom: 16 }}>
          <FormulaireFournisseur
            {...(saisie === 'nouveau' ? {} : { fournisseur: saisie })}
            onFerme={() => {
              setSaisie(null);
            }}
          />
        </div>
      )}

      <Sheet>
        {peut('fournisseur.creer') && saisie === null && (
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => {
                setSaisie('nouveau');
              }}
            >
              Nouveau fournisseur
            </button>
          </div>
        )}

        <div className="champ" style={{ marginBottom: 12, maxWidth: 340 }}>
          <label htmlFor="recherche-fournisseur">Nom ou téléphone</label>
          <input
            id="recherche-fournisseur"
            value={recherche}
            placeholder="Rechercher…"
            onChange={(evenement) => {
              setRecherche(evenement.target.value);
            }}
          />
        </div>

        <Erreur erreur={error} />
        <Erreur erreur={bascule.error} />

        {isPending ? (
          <Chargement />
        ) : (
          <Liste
            fournisseurs={data?.donnees ?? []}
            onModifier={peut('fournisseur.modifier') ? setSaisie : null}
            onBasculer={
              peut('fournisseur.desactiver')
                ? (fournisseur) => {
                    setABasculer(fournisseur);
                  }
                : null
            }
          />
        )}
      </Sheet>
    </>
  );
}

function Liste({
  fournisseurs,
  onModifier,
  onBasculer,
}: {
  fournisseurs: FournisseurListe[];
  onModifier: ((fournisseur: FournisseurListe) => void) | null;
  onBasculer: ((fournisseur: FournisseurListe) => void) | null;
}): ReactNode {
  const { ouvrir } = useApercuPdf();
  const colonnes: Colonne<FournisseurListe>[] = [
    { cle: 'nom', entete: 'Fournisseur', rendu: (f) => f.nom, texte: (f) => f.nom },
    {
      cle: 'telephone',
      entete: 'Téléphone',
      rendu: (f) => <span className="amount">{f.telephone ?? '—'}</span>,
      texte: (f) => f.telephone ?? '—',
    },
    {
      cle: 'ou',
      entete: 'Où',
      // Le repère prime sur l'adresse : au Bénin, c'est lui qui permet
      // de retrouver un entrepôt.
      rendu: (f) => f.repere ?? f.adresse ?? '—',
      texte: (f) => f.repere ?? f.adresse ?? '—',
    },
    {
      cle: 'receptions',
      entete: 'Réceptions',
      numerique: true,
      // Ce compte dit lesquels servent réellement, et repère une fiche
      // créée par erreur.
      rendu: (f) => <Nombre valeur={f.receptions} />,
      texte: (f) => String(f.receptions),
    },
    {
      cle: 'derniere',
      entete: 'Dernière',
      rendu: (f) =>
        f.derniere_reception === null ? '—' : <DateHeure valeur={f.derniere_reception} />,
      texte: (f) => (f.derniere_reception === null ? '—' : formaterDateHeure(f.derniere_reception)),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (f) =>
        f.actif ? <Badge statut="a_jour" libelle="Actif" /> : <Badge statut="inactif" />,
      texte: (f) => (f.actif ? 'Actif' : 'Inactif'),
    },
    // Le masquage est un CONFORT, pas une sécurité (RG-160) : le serveur
    // refuse de toute façon.
    ...(onModifier === null && onBasculer === null
      ? []
      : [
          {
            cle: 'actions',
            entete: '',
            rendu: (fournisseur: FournisseurListe) => (
                  <span className="ligne">
                    {onModifier !== null && (
                      <button
                        type="button"
                        className="bouton--discret"
                        onClick={() => {
                          onModifier(fournisseur);
                        }}
                      >
                        Modifier
                      </button>
                    )}
                    {onBasculer !== null && (
                      <button
                        type="button"
                        className="bouton--discret"
                        onClick={() => {
                          onBasculer(fournisseur);
                        }}
                      >
                        {fournisseur.actif ? 'Désactiver' : 'Réactiver'}
                      </button>
                    )}
                  </span>
            ),
          },
        ]),
  ];

  return (
    <>
      {fournisseurs.length > 0 && (
        <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <BoutonImprimer
            onClick={() => {
              ouvrir(() =>
                exporterTableauPdf({
                  titre: 'Fournisseurs',
                  colonnes,
                  lignes: fournisseurs,
                  nomFichier: 'fournisseurs',
                }),
              );
            }}
          />
        </div>
      )}
      <DataTable
        lignes={fournisseurs}
        cleDe={(fournisseur) => fournisseur.id}
        vide="Aucun fournisseur. Créez-en un, ou ils apparaîtront à la première réception."
        colonnes={colonnes}
      />
    </>
  );
}
